import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Form, Badge } from "react-bootstrap";
import { getSections, updateSectionPositionsV3 } from "../../services/sections";
import { getNewsBySectionIdForEdit, getLastActiveNews } from "../../services/news";
import { toast, ToastContainer } from "react-toastify";
import Loading from "../../components/layout/Loading";
import { TouchBackend } from "react-dnd-touch-backend";
import { isMobile } from "react-device-detect";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

const ITEM_TYPE = "NEWS_ITEM";

/**
 * Representa visualmente una noticia.
 * Se puede arrastrar si no está bloqueada.
 * Desde aquí se permite eliminarla o bloquearla si ya está en el tablero.
 */
const NewsCard = ({ news, index, type, moveNews, onRemove, onToggleLock }) => {
  // Habilita arrastre si no está bloqueada
  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { index, news, type },
    canDrag: () => !news.isLocked, // No permitir arrastrar si está bloqueado
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return (
    <Card ref={(node) => {
      if (!news?.isLocked && node) drag(node);
    }}
      className={`mt-3 mb-3 ${isDragging ? "border-primary bg-light opacity-50" : ""}`}
      style={{ cursor: news.isLocked ? "default" : "grab" }}
    >
      <Card.Body className="text-start">
        <h6 style={{ fontSize: "1rem", fontWeight: "bold" }}>{news.title}</h6>
        <b style={{ fontSize: ".8rem" }} >{news.short_description}</b>
      </Card.Body>
      <Card.Footer>
        <Row>
          <Col className="text-start">
            Categoría:{" "}
            <Badge bg="secondary">
              {news.news_category?.name}
              {news.subcategory_name ? `/${news.subcategory_name}` : null}
            </Badge>
          </Col>
        </Row>
        {type === "board" && (
          <>
            <Row className="mt-2">
              <Col className="text-start">
                ID: {news.id}
              </Col>

              <Col className="text-end">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => onRemove(index)}
                  disabled={news.isLocked}>
                  <i className="bi bi-trash"></i>
                </Button>
              </Col>
            </Row>
            <Row className="mt-1">
              <Col className="text-start">
                <Form.Check
                  type="checkbox"
                  label="Bloquear"
                  checked={news.isLocked}
                  onChange={() => onToggleLock(index)}
                />
              </Col>
            </Row>
          </>
        )}
      </Card.Footer>
    </Card>
  );
};

/**
 * Representa una "posición" en el tablero de noticias.
 * Acepta drops solo si no está bloqueada.
 */
const NewsSlot = ({ index, news, moveNews, addNewsToBoard, onRemove, onToggleLock }) => {
  const [{ isOver }, drop] = useDrop({
    accept: ITEM_TYPE,
    canDrop: () => !news?.isLocked,
    drop: (draggedItem) => {
      if (!news?.isLocked) {
        // Desde la lista lateral
        if (draggedItem.type === "list") {
          addNewsToBoard(draggedItem.news, index);
        } else {
          moveNews(draggedItem.index, index);
        }
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <div
      ref={drop}
      className={`border p-3 text-center h-100 ${isOver ? "bg-warning" : "bg-light"}`}
      style={{ minHeight: "80px" }}
    >
      Posición {index + 1}
      {news ? (
        <NewsCard
          news={news}
          index={index}
          type="board"
          moveNews={moveNews}
          onRemove={onRemove}
          onToggleLock={onToggleLock}
        />
      ) : null}
    </div>
  );
};

/**
 * Representa el tablero principal con las 20 posiciones.
 * Contiene lógica de movimiento, inserción, bloqueo y eliminación.
 */
const NewsBoard = ({ newsBoard, setNewsBoard, newsList, setNewsList }) => {
  // Mueve una noticia de una posición a otra si ambas no están bloqueadas
  const moveNews = (fromIndex, toIndex) => {
    const updatedBoard = [...newsBoard];

    // Si el destino está bloqueado, cancelamos el movimiento
    if (updatedBoard[toIndex]?.isLocked) {
      toast.warn("La posición de destino está bloqueada.");
      return;
    }

    // Si el origen está bloqueado, tampoco permitimos mover
    if (updatedBoard[fromIndex]?.isLocked) {
      toast.warn("No se puede mover una noticia bloqueada.");
      return;
    }

    // Intercambia si hay contenido, si no solo mueve
    if (updatedBoard[toIndex]) {
      const temp = updatedBoard[toIndex];
      updatedBoard[toIndex] = updatedBoard[fromIndex];
      updatedBoard[fromIndex] = temp;
    } else {
      updatedBoard[toIndex] = updatedBoard[fromIndex];
      updatedBoard[fromIndex] = null;
    }

    setNewsBoard(updatedBoard);
  };

  /**
   * Inserta una noticia desde la lista lateral a una posición específica del tablero.
   * Si hay otras noticias, las recorre una casilla adelante (ignorando bloqueadas).
   * Respeta bloqueos y elimina duplicados.
   */
  const addNewsToBoard = (newsItem, targetIndex) => {
    let updatedBoard = [...newsBoard];

    // 1. Si la posición de destino está bloqueada, no hacemos nada
    if (updatedBoard[targetIndex]?.isLocked) {
      toast.warn("La posición de destino está bloqueada.");
      return;
    }

    // 2. Eliminar duplicados desbloqueados
    const duplicateIndex = updatedBoard.findIndex(
      (item) => item?.id === newsItem.id && !item.isLocked
    );
    if (duplicateIndex !== -1) {
      updatedBoard[duplicateIndex] = null;
    }

    // 3. Recorrer a la derecha, saltando bloqueados
    let movableIndexes = [];
    for (let i = targetIndex; i < updatedBoard.length; i++) {
      if (!updatedBoard[i]?.isLocked) {
        movableIndexes.push(i);
      }
    }

    // Si no hay espacio para mover (todas las posiciones están bloqueadas), avisar
    if (movableIndexes.length === 0) {
      toast.warn("No hay espacio disponible para insertar.");
      return;
    }

    // Si el último espacio desbloqueado ya está ocupado, esa noticia se "cae"
    let droppedNews = null;
    if (updatedBoard[movableIndexes[movableIndexes.length - 1]]) {
      droppedNews = updatedBoard[movableIndexes[movableIndexes.length - 1]];
    }

    // Recorrer las noticias a la derecha (de atrás hacia adelante)
    for (let i = movableIndexes.length - 1; i > 0; i--) {
      updatedBoard[movableIndexes[i]] = updatedBoard[movableIndexes[i - 1]];
    }
    // Insertar la nueva noticia en la posición deseada
    updatedBoard[movableIndexes[0]] = { ...newsItem, isLocked: false };

    // Actualizar la lista lateral
    let newNewsList = newsList.filter((n) => n.id !== newsItem.id);
    if (droppedNews) {
      newNewsList = [...newNewsList, droppedNews];
    }

    setNewsBoard(updatedBoard);
    setNewsList(newNewsList);
  };

  // Elimina una noticia del tablero si no está bloqueada
  const removeNewsFromBoard = (index) => {
    const updatedBoard = [...newsBoard];

    // Evitar eliminar noticias bloqueadas
    if (updatedBoard[index]?.isLocked) return;

    const removedNews = updatedBoard[index];
    updatedBoard[index] = null;

    if (removedNews) {
      setNewsList([...newsList, removedNews]);
    }

    setNewsBoard(updatedBoard);
  };

  // Cambia el estado de bloqueo de una noticia en el tablero
  const toggleLock = (index) => {
    const updatedBoard = newsBoard.map((item, i) => {

      if (i === index && item) {
        return {
          ...item, // Clonamos la noticia para mantener inmutabilidad
          articleId: item.articleId ?? item.id, // Aseguramos que articleId siempre esté presente (prevención de errores)
          isLocked: !item.isLocked, // Invertimos el estado de bloqueo
        };
      }

      return item; // Las demás noticias no se tocan
    });

    // Igual: Actualizamos el estado global del tablero
    setNewsBoard(updatedBoard);
  };

  return (
    <div>
      <h4 className="mb-3">Posiciones de Noticias</h4>
      <Container>
        <Row>
          {newsBoard.map((news, index) => (
            <Col key={index} md={3} className="mb-3">
              <NewsSlot
                index={index}
                news={news}
                moveNews={moveNews}
                addNewsToBoard={addNewsToBoard}
                onRemove={removeNewsFromBoard}
                onToggleLock={toggleLock}
              />
            </Col>
          ))}
        </Row>
      </Container>
    </div>
  );
};

/**
 * Muestra la lista lateral de noticias listas para ser posicionadas.
 */
const NewsList = ({ newsList }) => {
  return (
    <div >
      <h4>Noticias Listas {newsList.length}</h4>
      {newsList.map((news, index) => (
        <NewsCard key={news.id} news={news} index={index} type="list" moveNews={() => { }} />
      ))}
    </div>
  );
};

/**
 * Componente raíz para gestionar secciones y posicionar noticias en un tablero de 20 espacios.
 * Permite cargar noticias, secciones, insertar, bloquear, mover y guardar estados al backend.
 */
const ManageSections = () => {
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(0);
  const [newsList, setNewsList] = useState([]);
  const [newsBoard, setNewsBoard] = useState(Array(20).fill(null));
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  /**
   * useEffect al montar el componente.
   * - Carga secciones disponibles desde la API.
   * - Si solo hay una sección, la selecciona automáticamente.
   * - En móviles, activa scroll automático al hacer drag cerca del borde.
   */
  useEffect(() => {
    const fetchSections = async () => {
      try {
        const data = await getSections();
        setSections(data);

        if (data.length == 1) {
          loadNewsBySection(data[0].id);
          setSelectedSection(data[0].id);
        }
      } catch {
        toast.error("Error al cargar secciones.");
      }
    };

    fetchSections();

    // Auto-scroll para dispositivos móviles durante drag
    const handleAutoScroll = (e) => {
      const threshold = 60; // px desde el borde
      const scrollSpeed = 80; // velocidad de scroll

      if (window.innerHeight - e.touches[0].clientY < threshold) {
        window.scrollBy(0, scrollSpeed);
      } else if (e.touches[0].clientY < threshold) {
        window.scrollBy(0, -scrollSpeed);
      }
    };

    if (isMobile) {
      document.addEventListener("touchmove", handleAutoScroll);
    }

    return () => {
      document.removeEventListener("touchmove", handleAutoScroll);
    };
  }, []);

  /**
   * Obtiene del backend las noticias asignadas a una sección y las coloca en el tablero.
   * - Respeta posiciones y bloqueos desde la base de datos.
   * - Filtra para que no haya noticias duplicadas en la lista lateral.
   */
  const loadNewsBySection = async (sectionId) => {
    setLoading(true); // Mostrar spinner o estado de carga mientras se obtiene la info

    try {
      // 1. Obtener noticias ya asignadas desde el backend
      const data = await getNewsBySectionIdForEdit(sectionId);

      // 2. Inicializar tablero vacío
      const updatedBoard = Array(20).fill(null);
      // 3. Ubicar cada noticia en su posición
      data.forEach((item) => {
        // Validación adicional para evitar posiciones inválidas (ej. texto, NaN, fuera de rango)
        const pos = parseInt(item.position, 10);
        if (!isNaN(pos) && pos >= 1 && pos <= 20) {
          updatedBoard[pos - 1] = {
            ...item,
            // Aseguramos que `id` y `articleId` estén disponibles para futuras referencias
            id: item.articleId ?? item.id,
            articleId: item.articleId ?? item.id,
            title: item.title ?? "Sin título",
            // Normalizamos isLocked como booleano (evita errores si viene como string o null)
            isLocked: !!item.isLocked,
            position: pos,
          };
        } else {
          // Se alerta si una posición viene corrupta
          console.warn("Posición inválida:", item.position, "ID:", item.id);
        }
      });

      // Se actualiza el tablero con las noticias posicionadas correctamente
      setNewsBoard(updatedBoard);

      // 4. Filtrar las noticias que ya están en el tablero para no duplicarlas en la lista lateral
      const allNews = await getLastActiveNews(70);

      // En su lugar, se extraen los IDs únicos del tablero
      const idsInBoard = updatedBoard
        .filter((slot) => slot && slot.id)
        .map((slot) => slot.id);

      // Luego se filtran solo las noticias que no están ya en el tablero
      const filteredNews = allNews.filter((n) => !idsInBoard.includes(n.id));
      setNewsList(filteredNews); // Se actualiza el listado lateral
    } catch {
      toast.error("Error al cargar noticias.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Se activa al seleccionar una nueva sección del dropdown.
   * Actualiza el ID seleccionado y recarga las noticias del backend para esa sección.
   */
  const handleSectionChange = (e) => {
    const sectionId = parseInt(e.target.value);
    setSelectedSection(sectionId);
    loadNewsBySection(sectionId);
  };

  /**
   * Guarda el estado actual del tablero (noticias posicionadas) en el backend.
   * Envía la posición, ID de noticia y estado de bloqueo.
   * Se ejecuta al hacer clic en "Guardar".
   */
  const saveNewsBoard = async () => {
    // Validación básica antes de continuar
    if (selectedSection === 0) {
      toast.error("Selecciona una sección antes de guardar.");
      return;
    }

    // Genera el payload de 20 posiciones con id y bloqueo
    const positions = newsBoard.map((news, index) => {
      const payload = {
        position: index + 1,
        // Se prioriza `articleId` (cuando ya fue asignado desde el backend)
        // Se usa `??` en lugar de `||` para evitar sobrescribir valores falsy válidos (como `0`)
        articleId: news?.articleId ?? news?.id ?? null,
        // Normalizamos `isLocked` como booleano (evita undefined o null)
        isLocked: news?.isLocked ?? false,
      };

      return payload;
    });

    try {
      setIsSaving(true); // Activar spinner de guardado
      await updateSectionPositionsV3({ positions, sectionId: selectedSection }); // Se envía payload limpio al backend
      toast.success("Noticias guardadas correctamente.");
      loadNewsBySection(selectedSection); // Refrescar el tablero después del guardado exitoso
      setIsSaving(false); // Detener spinner
    } catch {
      toast.error("Error al guardar las posiciones.");
      setIsSaving(false);
    }
  };

  return (
    <DndProvider backend={isMobile ? TouchBackend : HTML5Backend} options={isMobile ? { enableMouseEvents: true } : undefined}>
      <Container fluid >
        <Row>
          <Col xl={10}>
            <Form.Group controlId="formRole">
              <Form.Label>Secciones *:</Form.Label>
              <Form.Control as="select" value={selectedSection} onChange={handleSectionChange}>
                <option key={0} value={0}>Seleccionar sección</option>
                {sections.map((section) => (
                  <option key={section.id} value={section.id}>{section.name}</option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col className="d-flex justify-content-center align-items-center">
            <Button onClick={saveNewsBoard} disabled={selectedSection == 0 ? true : false} className="mt-3 w-100">
              {isSaving ? "Guardando" : "Guardar"}
            </Button>
          </Col>
        </Row>
        {/* Mostrar `Loading` si está cargando, de lo contrario, mostrar el contenido */}
        {loading ? (
          <Loading />
        ) : (
          <Row className="mt-3">
            <Col md={2} style={{ maxHeight: "2000px", overflowY: "auto" }}>
              <NewsList newsList={newsList} />
            </Col>
            <Col md={10}>
              <NewsBoard
                newsBoard={newsBoard}
                setNewsBoard={setNewsBoard}
                newsList={newsList}
                setNewsList={setNewsList}
              />
            </Col>
          </Row>
        )}
      </Container>
      <ToastContainer />
    </DndProvider>
  );
};

export default ManageSections;